/**
 * User Management Component
 * Admin interface for managing users
 */

import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';

interface User {
  id: string;
  email: string;
  role: 'ADMIN' | 'USER';
  isActive: boolean;
  statementCredits?: number;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
}

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getUsers();
      setUsers(response);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRole = async (userId: string, currentRole: string) => {
    const newRole = currentRole === 'ADMIN' ? 'USER' : 'ADMIN';

    if (!confirm(`Change user role to ${newRole}?`)) {
      return;
    }

    try {
      await adminAPI.updateUser(userId, { role: newRole });
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update user role');
    }
  };

  const handleToggleActive = async (userId: string, isActive: boolean) => {
    const action = isActive ? 'deactivate' : 'reactivate';

    if (!confirm(`Are you sure you want to ${action} this user?`)) {
      return;
    }

    try {
      await adminAPI.updateUser(userId, { isActive: !isActive });
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || `Failed to ${action} user`);
    }
  };

  const handleAddCredits = async (userId: string, email: string, currentCredits: number) => {
    const input = prompt(`Add credits for "${email}" (current: ${currentCredits}).
Enter number of credits to add:`, "3");
    if (input === null) return;

    const creditsToAdd = parseInt(input, 10);
    if (isNaN(creditsToAdd) || creditsToAdd <= 0) {
      alert("Please enter a valid positive number");
      return;
    }

    try {
      await adminAPI.updateUserCredits(userId, creditsToAdd, 'add');
      await loadUsers();
      alert(`Successfully added ${creditsToAdd} credit(s) to "${email}"`);
    } catch (err: any) {
      alert(err.response?.data?.error || "Failed to add credits");
    }
  };

  const handleDeleteUser = async (userId: string, email: string) => {
    if (!confirm(`Are you sure you want to delete user "${email}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminAPI.deleteUser(userId);
      await loadUsers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={loadUsers}
          className="mt-2 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">
            Manage user accounts and permissions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          Create User
        </button>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Credits
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Login
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {user.email}
                    </div>
                    <div className="text-xs text-gray-500">
                      Created {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleRole(user.id, user.role)}
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      user.role === 'ADMIN'
                        ? 'bg-purple-100 text-purple-800 hover:bg-purple-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {user.role}
                  </button>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleActive(user.id, user.isActive)}
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      user.isActive
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-red-100 text-red-800 hover:bg-red-200'
                    }`}
                  >
                    {user.isActive ? 'Active' : 'Inactive'}
                  </button>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {user.role === 'ADMIN' ? '∞' : (user.statementCredits ?? 3)}
                    </span>
                    {user.role !== 'ADMIN' && (
                      <button
                        onClick={() => handleAddCredits(user.id, user.email, user.statementCredits ?? 3)}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        +Add
                      </button>
                    )}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.lastLoginAt
                    ? new Date(user.lastLoginAt).toLocaleDateString()
                    : 'Never'}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => handleDeleteUser(user.id, user.email)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No users found
          </div>
        )}
      </div>

      {/* User Count */}
      <div className="text-sm text-gray-600">
        Total users: {users.length} (
        {users.filter((u) => u.role === 'ADMIN').length} admin
        {users.filter((u) => u.role === 'ADMIN').length !== 1 ? 's' : ''},
        {' '}{users.filter((u) => u.role === 'USER').length} regular)
      </div>
    </div>
  );
};
